import moment from 'moment';
import { Dispatch, SetStateAction, useCallback } from 'react';
import {
  PlanningLevelConfig,
  ResourceType,
} from 'api/resource-management/types';
import { AllocationModalParams } from 'components/organisms/resource-management/manage-allocation';
import { EventEditParams } from 'components/organisms/resource-management/scheduler-config/listeners';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { ExtendedManagerProps } from '../../types';

interface Props extends ExtendedManagerProps {
  workingDaysConfig: WorkingDaysConfiguration;
  setCurrentEvent: Dispatch<SetStateAction<AllocationEventModel | undefined>>;
  setAllocationModalParams: Dispatch<
    SetStateAction<AllocationModalParams | undefined>
  >;
}
type ReturnProps = (params: EventEditParams) => void;

export const useAllocationEditHandler = ({
  workingDaysConfig,
  setCurrentEvent,
  setAllocationModalParams,
  resourceManagementConfig,
}: Props): ReturnProps => {
  return useCallback(
    ({ event: allocationEvent }: EventEditParams) => {
      allocationEvent.set('active', true);
      setCurrentEvent(allocationEvent);
      const numberOfWorkingDays = numberOfWorkingDaysBetweenDates(
        moment(allocationEvent.startDate),
        moment(allocationEvent.endDate),
        workingDaysConfig,
      );
      const userId = allocationEvent.get('userId');
      const teamId = allocationEvent.get('teamId');
      const roleId = allocationEvent.get('roleId');
      const referenceTeamId = allocationEvent.get('referenceTeamId');
      const medium = allocationEvent.get('medium');

      setAllocationModalParams({
        ...(userId != null
          ? {
              type: ResourceType.User,
              userId,
              roleId,
              referenceTeamId,
            }
          : {
              teamId,
              roleId,
              type: ResourceType.Team,
            }),
        projectId: allocationEvent.get('projectId'),
        minutes:
          allocationEvent.get('minutes') *
          (resourceManagementConfig?.planningLevelConfig ===
          PlanningLevelConfig.HoursPerWeek
            ? Object.values(workingDaysConfig).filter(Boolean).length
            : 1),
        workingDays: numberOfWorkingDays,
        billable: allocationEvent.get('billable'),
        projectPhase: allocationEvent.get('projectPhase'),
        eventId: allocationEvent.get('id'),
        medium,
        startDate: allocationEvent.startDate,
        endDate: allocationEvent.endDate,
      });
    },
    [
      resourceManagementConfig?.planningLevelConfig,
      setAllocationModalParams,
      setCurrentEvent,
      workingDaysConfig,
    ],
  );
};
